import { useState } from "react";
import { ScrollView, View } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import {
  isTicketActive,
  TICKET_LIMITS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
} from "@brad-tipper/app-frontend/tickets";

import { Button } from "~/components/ui/Button";
import { ModalHeader } from "~/components/ui/ModalHeader";
import { RuleLine } from "~/components/ui/RuleLine";
import { Screen } from "~/components/ui/Screen";
import { BodyText, CaptionText, Heading3 } from "~/components/ui/Text";
import { TextField } from "~/components/ui/TextField";
import {
  useAddTicketNote,
  useSetTicketStatus,
  useTicket,
} from "~/hooks/useTickets";
import { spacing, useTheme } from "~/styles/theme";

export default function TicketScreen() {
  const { colors } = useTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const ticket = useTicket(id);
  const addNote = useAddTicketNote(id ?? "");
  const setStatus = useSetTicketStatus(id ?? "");
  const [note, setNote] = useState("");

  const data = ticket.data;
  const active = data ? isTicketActive(data.status) : true;

  async function send() {
    const body = note.trim();
    if (!body) return;
    await addNote.mutateAsync(body);
    setNote("");
  }

  return (
    <Screen>
      <ModalHeader
        title={data ? `Ticket #${data.number}` : "Ticket"}
        onClose={() => router.back()}
      />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.lg, gap: spacing.lg }}
      >
        {!data ? (
          <CaptionText color={ticket.error ? colors.danger : colors.muted}>
            {ticket.error ? "Unable to load this ticket." : "Loading…"}
          </CaptionText>
        ) : (
          <>
            <View style={{ gap: spacing.xxs }}>
              <Heading3>{data.title}</Heading3>
              <CaptionText color={colors.muted}>
                {TICKET_TYPE_LABELS[data.type]} ·{" "}
                {TICKET_STATUS_LABELS[data.status]} · opened{" "}
                {new Date(data.createdAt).toLocaleDateString()}
                {data.awaitingSupport ? " · waiting for our reply" : ""}
              </CaptionText>
            </View>
            <BodyText>{data.description}</BodyText>
            <RuleLine />
            <Heading3>Conversation</Heading3>
            {data.messages.length === 0 ? (
              <CaptionText color={colors.muted}>
                No notes yet. We will email you when we reply.
              </CaptionText>
            ) : (
              data.messages.map((message) => (
                <View key={message.id} style={{ gap: spacing.xxs }}>
                  <CaptionText color={colors.muted}>
                    {message.from === "support" ? "Support" : "You"} ·{" "}
                    {new Date(message.createdAt).toLocaleString()}
                  </CaptionText>
                  <BodyText>{message.body}</BodyText>
                </View>
              ))
            )}
            <RuleLine />
            <TextField
              label={active ? "Add a note" : "Add a note (reopens this ticket)"}
              value={note}
              onChangeText={setNote}
              maxLength={TICKET_LIMITS.note}
              multiline
              numberOfLines={4}
              style={{ height: 110, paddingTop: spacing.sm, textAlignVertical: "top" }}
              placeholder="Anything else we should know?"
            />
            {addNote.error ? (
              <CaptionText color={colors.danger}>
                {addNote.error instanceof Error
                  ? addNote.error.message
                  : "Unable to send."}
              </CaptionText>
            ) : null}
            <Button
              label={addNote.isPending ? "Sending…" : "Send note"}
              disabled={!note.trim() || addNote.isPending}
              fullWidth
              onPress={() => void send()}
            />
            <Button
              label={active ? "Close ticket" : "Reopen"}
              variant="secondary"
              disabled={setStatus.isPending}
              fullWidth
              onPress={() =>
                void setStatus.mutateAsync(active ? "closed" : "open")
              }
            />
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
